/**
 * T+ Watchlist Panel — stocks approaching setup conditions
 */
import React from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, LinearProgress, alpha, Tooltip,
} from '@mui/material';
import { Visibility, TrendingUp, TrendingFlat } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const thSx = { fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.02em', py: 0.8, borderBottom: '1px solid #e2e8f0', bgcolor: '#fafbfc' };
const tdSx = { py: 0.8, fontSize: '0.8rem', borderBottom: '1px solid #f1f5f9' };

const fmt = (n) => n ? n.toLocaleString('vi-VN', { maximumFractionDigits: 0 }) : '–';

const WatchlistPanel = ({ watchlist, loading }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  if (loading) {
    return (
      <Box sx={{ py: 5, textAlign: 'center' }}>
        <Typography sx={{ fontSize: '0.82rem', color: '#94a3b8' }}>
          {t('tplus.watchlist.scanning')}
        </Typography>
      </Box>
    );
  }

  if (!watchlist || watchlist.length === 0) {
    return (
      <Box sx={{ py: 5, px: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: '#f8fafc', border: '1.5px dashed #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Visibility sx={{ fontSize: 24, color: '#cbd5e1' }} />
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography sx={{ fontSize: '0.84rem', fontWeight: 600, color: '#94a3b8', lineHeight: 1.4 }}>
            {t('tplus.watchlist.emptyTitle')}
          </Typography>
          <Typography sx={{ fontSize: '0.72rem', color: '#cbd5e1', mt: 0.3, maxWidth: 420 }}>
            {t('tplus.watchlist.emptyDesc')}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {/* Summary bar */}
      <Box sx={{ px: 2.5, py: 1, borderBottom: '1px solid #f1f5f9', bgcolor: alpha('#8b5cf6', 0.04), display: 'flex', alignItems: 'center', gap: 1 }}>
        <Visibility sx={{ fontSize: 16, color: '#8b5cf6' }} />
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#8b5cf6' }}>
          {t('tplus.watchlist.approachingCount', { count: watchlist.length })}
        </Typography>
        <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', ml: 'auto' }}>
          {t('tplus.watchlist.sortedByReadiness')}
        </Typography>
      </Box>

      <TableContainer>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={thSx}>{t('tplus.header.ticker')}</TableCell>
              <TableCell sx={thSx}>{t('tplus.header.setup')}</TableCell>
              <TableCell align="center" sx={thSx}>{t('tplus.header.ready')}</TableCell>
              <TableCell align="right" sx={thSx}>{t('tplus.header.price')}</TableCell>
              <TableCell align="right" sx={thSx}>{t('tplus.header.support')}</TableCell>
              <TableCell align="right" sx={thSx}>{t('tplus.header.resistance')}</TableCell>
              <TableCell align="right" sx={thSx}>{t('tplus.header.distance')}</TableCell>
              <TableCell align="right" sx={thSx}>ATR%</TableCell>
              <TableCell align="center" sx={thSx}>Vol</TableCell>
              <TableCell sx={thSx}>{t('tplus.header.hint')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {watchlist.map((w, i) => {
              const setupClr = w.potential_setup === 'Spring' ? '#10b981' : w.potential_setup === 'NarrowSpring' ? '#059669' : w.potential_setup === 'MAConverge' ? '#0ea5e9' : w.potential_setup === 'ConfirmBreak' ? '#f59e0b' : '#8b5cf6';
              const readinessClr = w.readiness >= 70 ? '#10b981' : w.readiness >= 50 ? '#f59e0b' : '#94a3b8';
              const readinessLabel = w.readiness >= 70
                ? t('tplus.watchlist.readinessVeryHigh')
                : w.readiness >= 50
                  ? t('tplus.watchlist.readinessHigh')
                  : t('tplus.watchlist.readinessMedium');
              const volClr = w.vol_ratio >= 1.3 ? '#10b981' : w.vol_ratio >= 0.8 ? '#64748b' : '#f59e0b';

              return (
                <TableRow key={i} sx={{ '&:hover': { bgcolor: alpha(setupClr, 0.03) } }}>
                  <TableCell sx={tdSx}>
                    <Typography sx={{ fontWeight: 800, fontSize: '0.84rem', color: '#1e293b' }}>{w.ticker}</Typography>
                  </TableCell>
                  <TableCell sx={tdSx}>
                    <Chip
                      size="small"
                      icon={w.potential_setup === 'Spring' ? <TrendingFlat sx={{ fontSize: 14 }} /> : w.potential_setup === 'ConfirmBreak' ? <TrendingUp sx={{ fontSize: 14 }} /> : <TrendingUp sx={{ fontSize: 14 }} />}
                      label={t(`tplus.setup.${w.potential_setup}`, w.potential_setup)}
                      sx={{
                        height: 22, fontSize: '0.72rem', fontWeight: 700,
                        bgcolor: alpha(setupClr, 0.1), color: setupClr,
                        '& .MuiChip-icon': { color: setupClr },
                      }}
                    />
                  </TableCell>
                  <TableCell align="center" sx={tdSx}>
                    <Tooltip title={readinessLabel}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
                        <LinearProgress
                          variant="determinate"
                          value={w.readiness}
                          sx={{
                            width: 40, height: 6, borderRadius: 1,
                            bgcolor: '#e2e8f0',
                            '& .MuiLinearProgress-bar': { bgcolor: readinessClr, borderRadius: 1 },
                          }}
                        />
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, fontFamily: 'monospace', color: readinessClr, minWidth: 24 }}>
                          {w.readiness.toFixed(0)}
                        </Typography>
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right" sx={{ ...tdSx, fontFamily: 'monospace', fontWeight: 600 }}>
                    {fmt(w.close)}
                  </TableCell>
                  <TableCell align="right" sx={{ ...tdSx, fontFamily: 'monospace', fontWeight: 600, color: '#10b981' }}>
                    {fmt(w.support)}
                  </TableCell>
                  <TableCell align="right" sx={{ ...tdSx, fontFamily: 'monospace', fontWeight: 600, color: '#ef4444' }}>
                    {fmt(w.resistance)}
                  </TableCell>
                  <TableCell align="right" sx={tdSx}>
                    <Typography sx={{
                      fontSize: '0.78rem', fontWeight: 700, fontFamily: 'monospace',
                      color: w.dist_pct <= 1.0 ? '#10b981' : w.dist_pct <= 2.0 ? '#f59e0b' : '#94a3b8',
                    }}>
                      {w.dist_pct.toFixed(1)}%
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ ...tdSx, fontFamily: 'monospace', fontWeight: 600, color: '#64748b' }}>
                    {w.atr_pct.toFixed(1)}%
                  </TableCell>
                  <TableCell align="center" sx={tdSx}>
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, fontFamily: 'monospace', color: volClr }}>
                      {w.vol_ratio.toFixed(1)}x
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ ...tdSx, maxWidth: 200 }}>
                    <Typography sx={{ fontSize: '0.72rem', color: '#64748b', lineHeight: 1.3 }}>
                      {lang === 'vi' ? w.trigger_hint : w.trigger_hint_en}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default WatchlistPanel;
